import React, { useState, useEffect } from 'react';
import { Language_Text_Editor } from './language_text_editor.js';
import { Questions_Builder } from './questions_builder.js';
import { Question_Editor } from './question_editor.js';
import { Condition_Editor } from './condition_editor.js';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { Colors, generateHierarchicalCodes } from './form_builder.js';
import { useTranslation } from 'react-i18next';
import * as menu from "../../../assets/menus/menu.js";
import * as api from "../../../api/apiConnector.js";
import { useContext } from 'react';
import UserContext from '../../../components/User_Context.js';

/**
 * Section_Editor component
 *
 * @param {object} section - The section object to edit
 * @param {string} language - Current language ISO code (e.g., 'en', 'it')
 * @param {function} onConfirm - Callback(updatedSection)
 * @param {array} formSections - All sections from the full form (for condition editor)
 */
const Section_Editor = ({ section, language, onConfirm, formSections = [], colors = Colors, isEditing = false }) => {
    const [localSection, setLocalSection] = useState({
        name: [],
        description: [],
        questions: [],
        invisible: false,
        hydeName: false,
        mandatory: false,
        sectionCode: "",
        visibilityCondition: null
    });
    const { t } = useTranslation();
    const { currentUser } = useContext(UserContext);

    const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);

    // Helper function to extract plain text from EditorJS content
    const extractTextFromEditorJS = (content) => {
        if (!content) return '';
        
        try {
            // Try to parse as JSON (EditorJS format)
            const parsed = JSON.parse(content);
            if (parsed.blocks && Array.isArray(parsed.blocks)) {
                return parsed.blocks
                    .map(block => {
                        if (block.data && block.data.text) {
                            // Remove HTML tags from text
                            return block.data.text.replace(/<[^>]*>/g, '');
                        }
                        if (block.data && block.data.items) {
                            // Handle list items
                            return block.data.items.map(item => 
                                item.replace(/<[^>]*>/g, '')
                            ).join(', ');
                        }
                        return '';
                    })
                    .filter(text => text.trim())
                    .join(' ')
                    .trim();
            }
        } catch (e) {
            // If not JSON, treat as plain text and remove HTML tags
            return content.replace(/<[^>]*>/g, '').trim();
        }
        
        return content.toString().trim();
    };

    // Labels are now managed centrally in menu.js via form_builder_labels.js
    // No need to set up individual labels here as they are loaded automatically

    useEffect(() => {
        setLocalSection({
            name: section.name || [],
            description: section.description || [],
            questions: section.questions || [],
            invisible: section.invisible ?? false,
            hydeName: section.hydeName ?? false,
            mandatory: section.mandatory ?? false,
            sectionCode: section.sectionCode || "",
            visibilityCondition: section.visibilityCondition || null
        });

        // If there's no section code, generate a new one
        if (!section.sectionCode && formSections) {
            const sectionIndex = formSections.findIndex(s => s.id === section.id);
            if (sectionIndex !== -1) {
                setLocalSection(prev => ({
                    ...prev, 
                    sectionCode: generateHierarchicalCodes.generateSectionCode(sectionIndex)
                }));
            }
        }
    }, [section, formSections]);

    const handleConfirm = () => {
        onConfirm(localSection);
    };

    const updateQuestion = (index, updatedQuestion) => {
        const updatedQuestions = [...localSection.questions];
        updatedQuestions[index] = updatedQuestion;
        setLocalSection(prev => ({ ...prev, questions: updatedQuestions }));
    };

    // Nuovo metodo per ottenere entità vuote
    const getEmptyEntity = async (entityType) => {
        try {
            const result = await api.sendMessage("darm/form_builder/getEmptyEntity", {
                user: currentUser(),
                body: {
                    type: entityType // 'section', 'question', 'answer'
                }
            });

            if (result.success && result.entity) {
                return result.entity;
            }
            console.error(`Error getting empty ${entityType}:`, result);
            
            // Fallback in caso di errore nell'API
            if (entityType === 'section') {
                return { name: [], description: [], questions: [], id: await getGuid(), sectionCode: "", mandatory: false };
            } else if (entityType === 'question') {
                // For questions, we'll set the code in Questions_Builder when adding
                return { questionText: [], answers: [], id: await getGuid() };
            } else if (entityType === 'answer') {
                // For answers, we'll set the code in Answers_Builder when adding
                return { answerText: [], id: await getGuid() };
            }
            
            return null;
        } catch (err) {
            console.error(`Error calling empty ${entityType} generator:`, err);
            return null;
        }
    };

    // Function to get a GUID
    const getGuid = async () => {
        try {
            const result = await api.sendMessage("helper/guidGenerator", {
                user: currentUser(),
                body: {}
            });

            if (result.success && result.guid) {
                return result.guid;
            }
            console.error("Error getting GUID:", result);
            return null;
        } catch (err) {
            console.error("Error calling GUID generator:", err);
            return null;
        }
    };

    // Function to load available questions from previous sections for visibility conditions
    const loadAvailableQuestionsForSection = async () => {
        try {
            // Get the current section index
            const currentSectionIndex = formSections.findIndex(s => s.id === section.id);
            
            // Get all questions from previous sections only
            const availableQuestions = [];
            // Set per tenere traccia degli ID delle domande già aggiunte per evitare duplicati
            const addedQuestionIds = new Set();
            
            if (currentSectionIndex > 0) {
                for (let i = 0; i < currentSectionIndex; i++) {
                    const prevSection = formSections[i];
                    if (prevSection.questions && Array.isArray(prevSection.questions)) {
                        prevSection.questions.forEach(question => {
                            // Usa l'ID della domanda per evitare duplicati
                            const questionId = question.id || question.questionCode;
                            
                            // Se questa domanda è già stata aggiunta, saltala
                            if (addedQuestionIds.has(questionId)) {
                                return;
                            }
                            
                            // Extract readable text from EditorJS content
                            const questionTextContent = question.questionText?.find(t => t.language === language)?.text || 
                                                      question.questionText?.[0]?.text || '';
                            const readableText = extractTextFromEditorJS(questionTextContent) || 
                                               question.questionCode || 
                                               `Question ${question.id}`;
                            
                            // Process answers to match the expected structure for Condition_Editor
                            const processedAnswers = (question.answers || [])
                                .filter(answer => !answer.invisible) // Filter out invisible answers
                                .map(answer => {
                                    // Get answer text in current language
                                    const answerText = answer.answerText?.find(t => t.language === language)?.text || 
                                                     answer.answerText?.[0]?.text || 
                                                     answer.answerCode || 
                                                     `Answer ${answer.answerId || answer.id}`;
                                    
                                    return {
                                        id: answer.answerId || answer.id,
                                        text: answerText,
                                        code: answer.answerCode
                                    };
                                });
                            
                            availableQuestions.push({
                                id: questionId,
                                text: readableText,
                                code: question.questionCode,
                                answers: processedAnswers
                            });
                            
                            // Aggiungi l'ID al set per evitare duplicati futuri
                            addedQuestionIds.add(questionId);
                        });
                    }
                }
            }
            
            return availableQuestions;
        } catch (error) {
            console.error("Error loading available questions for section:", error);
            return [];
        }
    };

    // Function to clone a question
    const cloneQuestion = async (questionIndex) => {
        // Get the original question
        const originalQuestion = localSection.questions[questionIndex];
        
        // Get a new empty question entity from backend to get a new ID
        const emptyQuestion = await getEmptyEntity('question');
        if (!emptyQuestion) {
            console.error("Failed to get empty question");
            return;
        }
        
        // Create a deep clone of the question with a new ID
        const clonedQuestion = {
            ...originalQuestion,
            id: emptyQuestion.id,
            answers: [],
            // Generate a new question code based on position
            questionCode: generateHierarchicalCodes.generateQuestionCode(
                localSection.sectionCode, 
                localSection.questions.length
            )
        };
        
        // Add "(cloned)" or "(clonata)" to question text based on language
        // Handle both EditorJS content and plain text
        if (clonedQuestion.questionText && Array.isArray(clonedQuestion.questionText)) {
            clonedQuestion.questionText = clonedQuestion.questionText.map(textItem => {
                const suffix = textItem.language === 'it' ? ' (clonata)' : ' (cloned)';
                
                // Check if the text is EditorJS JSON format
                try {
                    const parsed = JSON.parse(textItem.text);
                    if (parsed.blocks && Array.isArray(parsed.blocks)) {
                        // It's EditorJS format - add suffix to the last block's text
                        const blocks = [...parsed.blocks];
                        if (blocks.length > 0) {
                            const lastBlock = { ...blocks[blocks.length - 1] };
                            if (lastBlock.data && lastBlock.data.text) {
                                lastBlock.data = {
                                    ...lastBlock.data,
                                    text: lastBlock.data.text + suffix
                                };
                                blocks[blocks.length - 1] = lastBlock;
                            }
                        } else {
                            // No blocks, add a new paragraph block with the suffix
                            blocks.push({
                                type: 'paragraph',
                                data: { text: suffix }
                            });
                        }
                        
                        return {
                            ...textItem,
                            text: JSON.stringify({
                                ...parsed,
                                blocks: blocks
                            })
                        };
                    }
                } catch (e) {
                    // Not JSON or invalid JSON, treat as plain text
                }
                
                // Plain text or invalid JSON - just append suffix
                return {
                    ...textItem,
                    text: textItem.text + suffix
                };
            });
        }
        
        // Clone all answers with new IDs
        for (let i = 0; i < originalQuestion.answers?.length || 0; i++) {
            const originalAnswer = originalQuestion.answers[i];
            
            // Get a new empty answer entity from backend
            const emptyAnswer = await getEmptyEntity('answer');
            if (!emptyAnswer) {
                console.error("Failed to get empty answer");
                continue; // Skip this answer
            }
            
            // Create a cloned answer with a new ID
            const clonedAnswer = {
                ...originalAnswer,
                id: emptyAnswer.id,
                answerCode: generateHierarchicalCodes.generateAnswerCode(
                    clonedQuestion.questionCode, 
                    clonedQuestion.answers.length
                )
            };
            
            clonedQuestion.answers.push(clonedAnswer);
        }
        
        // Add the cloned question to the section
        const updatedQuestions = [...localSection.questions, clonedQuestion];
        setLocalSection(prev => ({ ...prev, questions: updatedQuestions }));
    };

    if (editingQuestionIndex !== null) {
        return (
            <Question_Editor
                question={localSection.questions[editingQuestionIndex]}
                language={language}
                formSections={formSections}
                currentLocalSection={localSection}
                onConfirm={(updatedQuestion) => {
                    updateQuestion(editingQuestionIndex, updatedQuestion);
                    setEditingQuestionIndex(null);
                }}
                colors={colors}
                isEditing={true}
            />
        );
    }

    // Specific edit context colors for sections
    const editContextColors = colors.editContextSection;

    return (
        <div className="card shadow w-100" style={{ borderColor: editContextColors.border, borderWidth: '2px' }}>
            <div className="card-header d-flex justify-content-between align-items-center" 
                style={{ backgroundColor: editContextColors.header, color: colors.white }}>
                <h5 className="mb-0">
                    <span className="badge bg-white text-dark me-2">{t('editingSection', { ns: 'labels' })}</span>
                    {localSection.name.find(n => n.language === language)?.text || t('newSection', { ns: 'labels' })}
                </h5>
                <div>
                    <button className="btn btn-sm ms-2" 
                        style={{ backgroundColor: colors.white, color: colors.dark }}
                        onClick={handleConfirm}>
                        <FaCheck className="me-1" />
                        {t('done', { ns: 'labels' })}
                    </button>
                </div>
            </div>

            <div className="card-body">
                <div className="row g-3 w-100 mx-0">
                    <div className="col-12 px-0">
                        <div className="card w-100" style={{ backgroundColor: editContextColors.body }}>
                            <div className="card-header" 
                                style={{ backgroundColor: colors.secondaryLight }}>
                                <h6 className="mb-0">{t('sectionDetails', { ns: 'labels' })}</h6>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <label className="form-label fw-bold">{t('sectionName', { ns: 'labels' })} ({language})</label>
                                    <Language_Text_Editor
                                        language={language}
                                        mode="input"
                                        text={localSection.name}
                                        onChange={(updated) => setLocalSection(prev => ({ ...prev, name: updated }))}
                                        colors={colors}
                                    />
                                </div>

                                <div className="mb-3">
                                    <div className="form-check">
                                        <input 
                                            className="form-check-input" 
                                            type="checkbox" 
                                            id="sectionNoName"
                                            checked={localSection.hydeName}
                                            onChange={(e) => setLocalSection(prev => ({ ...prev, hydeName: e.target.checked }))}
                                        />
                                        <label className="form-check-label" htmlFor="sectionNoName">
                                            {t('noName', { ns: 'labels' })}
                                        </label>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <div className="form-check">
                                        <input 
                                            className="form-check-input" 
                                            type="checkbox" 
                                            id="sectionInvisible"
                                            checked={localSection.invisible}
                                            onChange={(e) => setLocalSection(prev => ({ ...prev, invisible: e.target.checked }))}
                                        />
                                        <label className="form-check-label" htmlFor="sectionInvisible">
                                            {t('invisible', { ns: 'labels' })}
                                        </label>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <div className="form-check">
                                        <input 
                                            className="form-check-input" 
                                            type="checkbox" 
                                            id="sectionMandatory"
                                            checked={localSection.mandatory}
                                            onChange={(e) => setLocalSection(prev => ({ ...prev, mandatory: e.target.checked }))}
                                        />
                                        <label className="form-check-label" htmlFor="sectionMandatory">
                                            {t('mandatory', { ns: 'labels' })}
                                        </label>
                                    </div>
                                </div>


                                <div className="mb-3">
                                    <label className="form-label fw-bold">{t('sectionCode', { ns: 'labels' })}</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={localSection.sectionCode}
                                        onChange={(e) => setLocalSection(prev => ({ ...prev, sectionCode: e.target.value }))}
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-bold">{t('sectionDescription', { ns: 'labels' })} ({language})</label>
                                    <Language_Text_Editor
                                        language={language}
                                        mode="textarea"
                                        text={localSection.description}
                                        onChange={(updated) => setLocalSection(prev => ({ ...prev, description: updated }))}
                                        colors={colors}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 px-0">
                        <div className="card w-100" style={{ backgroundColor: editContextColors.body }}>
                            <div className="card-header" 
                                style={{ backgroundColor: colors.secondaryLight }}>
                                <h6 className="mb-0">{t('visibilityConditions', { ns: 'labels' })}</h6>
                            </div>
                            <div className="card-body">
                                <p className="text-muted small mb-3">
                                    {t('visibilityConditionsDescription', { ns: 'labels' })}
                                </p>
                                <Condition_Editor
                                    condition={localSection.visibilityCondition}
                                    onChange={(updatedCondition) => {
                                        setLocalSection(prev => ({ 
                                            ...prev, 
                                            visibilityCondition: updatedCondition 
                                        }));
                                    }}
                                    loadAvailableQuestions={loadAvailableQuestionsForSection}
                                    colors={colors}
                                    parentEditContext="section"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="col-12 px-0">
                        <Questions_Builder
                            questions={localSection.questions}
                            language={language}
                            parentSectionCode={localSection.sectionCode}
                            onTextChange={(index, updatedTextArray) => {
                                const updated = [...localSection.questions];
                                updated[index] = {
                                    ...updated[index],
                                    questionText: updatedTextArray
                                };
                                setLocalSection(prev => ({ ...prev, questions: updated }));
                            }}
                            onDelete={(index) => {
                                const updated = [...localSection.questions];
                                updated.splice(index, 1);
                                
                                // After deletion, regenerate all question codes and answer codes
                                const updatedWithCodes = updated.map((question, qIndex) => {
                                    const newQuestionCode = generateHierarchicalCodes.generateQuestionCode(localSection.sectionCode, qIndex);
                                    
                                    // Also regenerate answer codes for this question
                                    const updatedAnswers = (question.answers || []).map((answer, aIndex) => ({
                                        ...answer,
                                        answerCode: generateHierarchicalCodes.generateAnswerCode(newQuestionCode, aIndex)
                                    }));
                                    
                                    return {
                                        ...question,
                                        questionCode: newQuestionCode,
                                        answers: updatedAnswers
                                    };
                                });
                                
                                setLocalSection(prev => ({ ...prev, questions: updatedWithCodes }));
                            }}
                            onAdd={async () => {
                                const emptyQuestion = await getEmptyEntity('question');
                                if (!emptyQuestion) {
                                    console.error("Failed to get empty question");
                                    return;
                                }
                                
                                // Assign a question code based on the section code and its position
                                const questionIndex = localSection.questions.length;
                                const questionCode = generateHierarchicalCodes.generateQuestionCode(
                                    localSection.sectionCode,
                                    questionIndex
                                );
                                
                                const questionWithCode = {
                                    ...emptyQuestion,
                                    questionCode
                                };
                                
                                const updated = [...localSection.questions, questionWithCode];
                                setLocalSection(prev => ({ ...prev, questions: updated }));
                            }}
                            onReorder={(reordered) => {
                                // The question codes are updated in the Questions_Builder component
                                setLocalSection(prev => ({ ...prev, questions: reordered }));
                            }}
                            onEdit={(index) => {
                                setEditingQuestionIndex(index);
                            }}
                            onClone={cloneQuestion}
                            colors={colors}
                            parentEditContext="section"
                            isEditing={true}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export { Section_Editor };
